import {
  Fragment,
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactElement,
} from "react";
import { useTranslation } from "react-i18next";
import { Button, Form } from "antd";
import { useCreateProtocolForm } from "../../../../..";
import PlatFormAPIAxiosInstance from "../../../../../../../shared/utils/platFormAxios";
import { TreatmentCard } from "./TreatmentCard";
import { TreatmentDetailsCard } from "./TreatmentDetailsCard";
import SkeleteonCpCard from "./SkeletonCpCard";
import { formatTranslation } from "../../../../../../../shared/utils/translationUtils";

// Normalize a product (variety) name for cross-country matching:
// treat underscores/hyphens as spaces (naming conventions vary by source),
// collapse whitespace, lowercase. Mirrors the seed-screen logic so a cosmetic
// difference ("ProductX " vs "productx", "SY_ZOOMBA" vs "SY Zoomba") can't
// trigger a false drop.
const normalizeName = (value: unknown): string =>
  String(value ?? "")
    .trim()
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .toLowerCase();

// A product row is "real" once the user has actually picked a product.
// Empty placeholder rows ({ value: "", rate: 0 }) are NOT real and must be
// preserved as-is so the add-treatment / empty-row UX isn't broken.
const isRealProduct = (p: any): boolean => Boolean(p?.variety || p?.value);

const getBiologicalsProductOptions = (products: any[]): any[] => {
  let options: any[] = [];
  if (Array.isArray(products)) {
    options = products?.map((product: any) => {
      return {
        label: (
          <div className="bio-product-option-label">
            <div className="product-name">{product?.name}</div>
            <div className="product-indication">{product?.indication}</div>
          </div>
        ),
        value: product?.id,
        key: product?.id,
        disabled: false,
        variety: product?.name,
        measure_unit: product?.selling_units,
      };
    });
  }
  return options;
};

interface SelectCpProductProps {
  loading?: boolean;
  country?: string;
  orgId?: string;
}

const SelectCpProduct = ({
  loading = false,
  country = "",
  orgId = "",
}: SelectCpProductProps): ReactElement => {
  const { form, setFormValidationData, formValidationData } =
    useCreateProtocolForm();
  const { t } = useTranslation();
  const [isLoading] = useState<boolean>(false);
  const [productOptions, setProductOptions] = useState<any>([]);
  const [opened, setOpened] = useState<any>([0]);
  const [search, setSearch] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [formData, setFormData] = useState<any>([
    {
      products: [{ value: "", rate: 0 }],
      tank_mix_rate: {
        min: 0,
        max: 0,
      },
      instructions: "",
    },
  ]);

  // Armed only when a template-driven country change requests bio
  // revalidation. Prevents the revalidation effect from matching selected
  // products against the PREVIOUS country's catalog before the new one loads.
  const bioRevalidationPendingRef = useRef<boolean>(false);

  // (race fix) Monotonically increasing id for every product-catalog fetch.
  // Only the response from the LATEST fetch is allowed to update
  // `productOptions`. Without this, the old-country fetch and the
  // new-country (revalidation) fetch can resolve out of order, leaving a stale
  // catalog that the matching then runs against.
  const latestCatalogReqRef = useRef<number>(0);

  // (race fix) Holds the request id of the fetch that was kicked off
  // specifically for revalidation. Only THIS exact response is allowed to
  // trigger `reconcileBioRevalidation` — not any other concurrent fetch
  // (search-debounce, mount) that also resolves against `productOptions`.
  const revalidationReqIdRef = useRef<number | null>(null);

  // Immutable snapshot of the ORIGINAL (template-default) treatments, captured
  // once on first load. Used to restore treatments + rates when the user
  // returns to the original country. We snapshot here (not the live form) since
  // remap/clear operations mutate the form values in place.
  const originalTreatmentsRef = useRef<any[] | null>(null);

  function handleChangeFormData(data: any, refresh?: boolean): void {
    setFormData([...data]);
    updateBioProductFormData(data);
    if (refresh && search.trim().length > 0) {
      setSearch("");
    }
  }

  function handleEdit(index: number): void {
    setOpened([...opened, index]);
  }

  useEffect(() => {
    if (country && orgId) {
      fetchProducts().catch((error) => {
        console.error("Error fetching data:", error);
      });
    }
  }, [country, orgId]);

  // The persisted `biologicals.treatments` field is populated by a SEPARATE,
  // independent loader (the protocol/template fetch in the parent wizard,
  // which even has its own artificial delay) than the catalog-loading flag
  // below. Watching the field directly -- rather than only reacting to
  // `loading`/`isLoading` -- means this re-runs once the real data actually
  // arrives, instead of permanently locking in whatever was in the field
  // (often still empty) the moment the catalog happened to finish loading.
  const watchedTreatments = Form.useWatch(["biologicals", "treatments"], form);

  useEffect(() => {
    if (!loading && !isLoading) {
      setInitialData();
    }
  }, [loading, isLoading, watchedTreatments]);

  function setInitialData(): void {
    // Once a NON-EMPTY baseline has been captured, later re-fires of the watch
    // above (e.g. the user's own edits writing back into the form field)
    // should NOT re-run this -- it exists only to pick up the persisted data
    // once, whenever it actually finishes arriving. We deliberately gate on a
    // non-empty array (not merely `!== null`) so that if the baseline is still
    // empty, a later run can still capture the persisted treatments once they
    // arrive (which is exactly what the `Form.useWatch` above is for).
    if (
      Array.isArray(originalTreatmentsRef.current) &&
      originalTreatmentsRef.current.length > 0
    ) {
      return;
    }

    const formValues = form.getFieldsValue(true);
    const treatments = formValues?.biologicals?.treatments || [];
    if (treatments?.length > 0) {
      setOpened([]);
      const mapped = treatments.map((treatment: any) => ({
        ...treatment,
        products: treatment.products?.map((product: any) => ({
          ...product,
          value: product.id ?? product.value,
        })),
      }));
      // Capture the original treatments exactly once, for later restore.
      originalTreatmentsRef.current = mapped;
      setFormData([...mapped]);
    } else {
      // Sync the empty state only ONCE. `updateBioProductFormData(null)` below
      // writes to the same watched `biologicals.treatments` field, which would
      // otherwise re-fire this effect (fresh `[]` reference each time) and
      // retrigger the empty sync repeatedly. Arming an empty-array sentinel on
      // `originalTreatmentsRef` prevents that loop. A later run in which real
      // treatments have arrived takes the `if (treatments.length > 0)` branch
      // above instead -- which has no such guard -- so the real baseline is
      // still captured.
      if (
        Array.isArray(originalTreatmentsRef.current) &&
        originalTreatmentsRef.current.length === 0
      ) {
        return;
      }
      setOpened([0]);
      // The form's `biologicals.treatments` field may still hold data from an
      // earlier, unrelated write (e.g. a stale fetch). The visible UI is about
      // to show an empty treatment, so the form field -- which is what the
      // completion check (isBiologicalHasValidData) reads -- must agree, or
      // the tab can be marked complete while nothing is actually shown.
      updateBioProductFormData(null);
      // Arm the one-time empty sentinel (see comment above).
      originalTreatmentsRef.current = [];
    }
  }

  function updateBioProductFormData(data: any): void {
    const formValues = form.getFieldsValue(true);
    if (data) {
      form.setFieldsValue({
        biologicals: {
          ...formValidationData?.biological,
          treatments: data?.map((item: any) => ({
            products: item?.products?.map((product: any) => ({
              id: product?.value,
              variety: product?.variety,
              rate: product?.rate,
              measure_unit: product?.measure_unit,
            })),
            tank_mix_rate: {
              min: item?.tank_mix_rate?.min,
              max: item?.tank_mix_rate?.max,
            },
            instructions: item?.instructions,
          })),
        },
      });
      setFormValidationData((prev: any) => ({
        ...(prev ?? {}),
        biological: {
          ...(prev?.biological ?? {}),
          treatments: data?.map((item: any) => ({
            products: item?.products?.map((product: any) => ({
              id: product?.value,
              variety: product?.variety,
              rate: product?.rate,
              measure_unit: product?.measure_unit,
            })),
            tank_mix_rate: {
              min: item?.tank_mix_rate?.min,
              max: item?.tank_mix_rate?.max,
            },
            instructions: item?.instructions,
          })),
        },
      }));
    } else {
      form.setFieldsValue({
        biologicals: { ...formValues?.biologicals, treatments: [] },
      });
      setFormValidationData((prev: any) => ({
        ...(prev ?? {}),
        biologicals: [],
      }));
    }
  }

  useEffect(() => {
    const handler = setTimeout(() => {
      if (country && orgId) {
        fetchProducts(search.trim().length > 0 ? search : undefined).catch(
          (error) => {
            console.error("Error fetching products:", error);
          }
        );
      }
    }, 400);

    return () => {
      clearTimeout(handler);
    };
  }, [search, country, orgId]);

  // Per-product revalidation result handler (Scenario A). Called directly
  // from `fetchProducts` with the catalog IT just fetched, rather than via a
  // separate effect watching shared `productOptions` state -- any unrelated
  // concurrent fetch (search-debounce, mount) also writes `productOptions`,
  // so a second effect could fire with a catalog that isn't actually the
  // revalidation response, or never fire at all if its own state update gets
  // batched oddly relative to an interleaved unrelated fetch. Reconciling
  // inline with the exact data this fetch received removes that race.
  // - each currently selected (real) product that still exists by name in
  //   the new catalog is kept, remapping value/variety/measure_unit and
  //   leaving its rate untouched,
  // - a product that no longer exists is dropped individually (its rate goes
  //   with it) -- other products in the same or other treatments are
  //   unaffected, keeping their existing rate/tank_mix_rate/instructions,
  // - a treatment that had real product(s) but loses ALL of them is dropped
  //   (nothing left to mix/rate); a treatment that never had a real product
  //   (e.g. an empty add-treatment row) passes through untouched,
  // - if every treatment ends up with nothing real left, fall back to a
  //   single empty treatment so the add-product UX still has a row.
  const reconcileBioRevalidation = (options: any[]): void => {
    bioRevalidationPendingRef.current = false;

    // Index the new catalog by normalized variety name so cosmetic
    // case/whitespace differences don't cause a false drop.
    const byName = new Map<string, any>(
      options.map((o: any) => [normalizeName(o.variety), o])
    );

    // Read the CURRENT treatments from the form itself, not the `formData`
    // component state. `formData` is only populated once `setInitialData`
    // runs, which is gated behind THIS component's own catalog-loading flag
    // and can resolve slower than this revalidation fetch. Reconciling
    // against a still-default, not-yet-initialized `formData` would find
    // "nothing real, no mismatch" and then WRITE that empty placeholder back
    // into the form via `handleChangeFormData`, permanently displacing the
    // real persisted selection the moment it would otherwise have arrived.
    // The form field is written directly by the wizard's protocol/template
    // loader independently of this component's own init timing, so it's the
    // reliable source here (mirrors the equivalent seed-side fix).
    const formTreatments: any[] =
      form.getFieldsValue(true)?.biologicals?.treatments || [];
    const baseTreatments =
      formTreatments.length > 0 ? formTreatments : formData;

    const selectedProducts = baseTreatments.flatMap((treatment: any) =>
      (treatment.products ?? []).filter(isRealProduct)
    );

    if (selectedProducts.length === 0) {
      // Nothing real to reconcile yet -- either genuinely no biological
      // products, or the persisted data simply hasn't reached the form yet.
      // Don't touch `formData`/the form in either case; just release the
      // guard and let the normal init/watch path populate things naturally.
      setFormValidationData((prev: any) => ({
        ...(prev ?? {}),
        bioRevalidationRequired: false,
      }));
      return;
    }

    // Drop only the individual products that no longer match the new
    // country's catalog; remap the ones that still match.
    const reconciled = baseTreatments
      .map((treatment: any) => {
        const originalProducts = treatment.products ?? [];
        const hadRealProduct = originalProducts.some(isRealProduct);
        const products = originalProducts
          .map((p: any) => {
            if (!isRealProduct(p)) return p; // preserve placeholder rows
            const match = byName.get(normalizeName(p?.variety));
            if (!match) return null; // mismatched -> drop this product + its rate
            return {
              ...p, // preserve rate
              value: match.value,
              variety: match.variety,
              measure_unit: match.measure_unit,
            };
          })
          .filter((p: any) => p !== null);
        return { ...treatment, products, hadRealProduct };
      })
      // Only drop a treatment that HAD a real product and lost all of them;
      // a treatment that never had one (empty row) passes through as-is.
      .filter(
        (treatment: any) =>
          !treatment.hadRealProduct || treatment.products.some(isRealProduct)
      )
      .map(({ hadRealProduct, ...treatment }: any) => treatment);

    if (reconciled.length === 0) {
      // Every treatment lost all of its real products -- fall back to a
      // single empty treatment so the add-product UX still has a row.
      clearTreatments();
      setFormValidationData((prev: any) => ({
        ...(prev ?? {}),
        bioRevalidationRequired: false,
      }));
      return;
    }

    handleChangeFormData(reconciled);
    setOpened(reconciled.map((_: any, i: number) => i));
    // This reconciled selection IS now the authoritative baseline -- prevent
    // `setInitialData` from later re-deriving (and potentially fighting)
    // it once its own gating conditions catch up. Treat an empty-array
    // sentinel the same as `null` here so a prior empty sync doesn't block
    // capturing the real reconciled baseline.
    if (
      originalTreatmentsRef.current === null ||
      (Array.isArray(originalTreatmentsRef.current) &&
        originalTreatmentsRef.current.length === 0)
    ) {
      originalTreatmentsRef.current = reconciled;
    }

    setFormValidationData((prev: any) => ({
      ...(prev ?? {}),
      bioRevalidationRequired: false,
    }));
  };

  // (race fix) `isRevalidation` marks the fetch that was triggered to reload
  // the catalog for cross-country revalidation. Its request id is recorded so
  // only THAT exact response (not any other concurrent fetch) triggers the
  // reconcile above.
  const fetchProducts = async (
    searchTerm?: string,
    isRevalidation = false
  ): Promise<void> => {
    // Claim a request id BEFORE awaiting. Any earlier in-flight request becomes
    // stale the moment a newer one starts.
    const reqId = ++latestCatalogReqRef.current;
    if (isRevalidation) {
      revalidationReqIdRef.current = reqId;
    }

    setSearchLoading(true);
    try {
      let url = `/catalog/orgs/${orgId}/products?full=true&country=${country}`;
      if (searchTerm && searchTerm.trim().length > 0) {
        url += `&name=${encodeURIComponent(searchTerm)}`;
      }
      const response = await PlatFormAPIAxiosInstance.get(url);

      const options = getBiologicalsProductOptions(
        response?.data?.content || []
      );

      // If THIS response is the revalidation fetch we were waiting on,
      // reconcile immediately -- regardless of whether some unrelated LATER
      // fetch (search-debounce, mount) has since become "the latest" per
      // `latestCatalogReqRef`. Reconciliation resolves `bioRevalidationRequired`,
      // a completely different piece of state than "what catalog is
      // currently displayed"; gating it behind that staleness check meant an
      // unrelated fetch racing ahead could get this response dropped before
      // it ever reconciled, leaving `bioRevalidationRequired` stuck `true`
      // forever.
      if (revalidationReqIdRef.current === reqId) {
        revalidationReqIdRef.current = null;
        if (bioRevalidationPendingRef.current) {
          reconcileBioRevalidation(options);
        }
      }

      // Drop out-of-order responses for the VISIBLE catalog only.
      if (reqId !== latestCatalogReqRef.current) {
        return;
      }

      setProductOptions(options);
    } catch (error) {
      // This try/catch means `fetchProducts` never rejects, so the arm
      // effect's `.catch()` on this call can never fire -- a network
      // failure here must reset the guard itself, or a failed revalidation
      // fetch would leave `bioRevalidationRequired` stuck `true` forever.
      if (revalidationReqIdRef.current === reqId) {
        revalidationReqIdRef.current = null;
        bioRevalidationPendingRef.current = false;
        setFormValidationData((prev: any) => ({
          ...(prev ?? {}),
          bioRevalidationRequired: false,
        }));
      }
      // Ignore stale failures too.
      if (reqId !== latestCatalogReqRef.current) {
        return;
      }
      setProductOptions([]);
    } finally {
      // Only the latest fetch owns the loading flag.
      if (reqId === latestCatalogReqRef.current) {
        setSearchLoading(false);
      }
    }
  };

  const handleSearch = useCallback((value: string) => {
    setSearch(value);
  }, []);

  function handleAddTreatment(): void {
    const newTreatment = {
      products: [{ value: "", rate: 0 }],
      tank_mix_rate: {
        min: 0,
        max: 0,
      },
      instructions: "",
    };
    handleEdit(formData.length);
    setSearch("");
    handleChangeFormData([...formData, newTreatment]);
  }

  function handleRemoveTreatment(index: number): void {
    const newTreatment = formData.filter(
      (item: any, pIndex: number) => pIndex !== index
    );
    handleChangeFormData(newTreatment);
  }

  // Resets all CP/biological treatments and their rates back to a single empty
  // treatment. Used on a template-driven country change.
  const clearTreatments = (): void => {
    const empty = [
      {
        products: [{ value: "", rate: 0 }],
        tank_mix_rate: { min: 0, max: 0 },
        instructions: "",
      },
    ];
    setFormData(empty);
    setOpened([0]);
    setSearch("");
    form.setFieldsValue({ biologicals: { treatments: [] } });
    setFormValidationData((prev: any) => ({
      ...(prev ?? {}),
      biological: { ...(prev?.biological ?? {}), treatments: [] },
    }));
  };

  // Returning to the original country -> restore the original treatments and
  // their rates from the immutable snapshot, then clear the restore flag.
  useEffect(() => {
    if (formValidationData?.restoreOriginalBio) {
      // Tear down any in-flight revalidation so it can't fire afterwards.
      bioRevalidationPendingRef.current = false;
      revalidationReqIdRef.current = null;

      if (
        originalTreatmentsRef.current &&
        originalTreatmentsRef.current.length > 0
      ) {
        handleChangeFormData(originalTreatmentsRef.current);
        setOpened([]);
      } else {
        clearTreatments();
      }
      setFormValidationData((prev: any) => ({
        ...(prev ?? {}),
        restoreOriginalBio: false,
      }));
    }
  }, [formValidationData?.restoreOriginalBio]);

  // Arm the revalidation guard the moment a country change requests it, and
  // kick off a DEDICATED catalog fetch for the new country. `fetchProducts`
  // reconciles inline as soon as THIS exact fetch resolves (see
  // `reconcileBioRevalidation` above). Wait for `country`/`orgId` to actually
  // be populated first -- they arrive asynchronously as props, and firing
  // against an empty org/country builds a malformed URL that returns no
  // (or wrong) products, making every real selection look mismatched. This
  // effect re-fires once they're ready since they're in the dependency array.
  useEffect(() => {
    if (!formValidationData?.bioRevalidationRequired) return;
    if (!country || !orgId) return;

    bioRevalidationPendingRef.current = true;
    // Clear search so the revalidation fetch returns the full new-country
    // catalog (not a name-filtered subset).
    setSearch("");
    void fetchProducts(undefined, true);
  }, [formValidationData?.bioRevalidationRequired, country, orgId]);

  // Scenario B coordination: crop was removed in the new country -> wipe all.
  useEffect(() => {
    if (formValidationData?.clearProductsAndCalculations) {
      bioRevalidationPendingRef.current = false;
      revalidationReqIdRef.current = null;
      clearTreatments();
      setFormValidationData((prev: any) => ({
        ...(prev ?? {}),
        clearProductsAndCalculations: false,
        bioRevalidationRequired: false,
      }));
    }
  }, [formValidationData?.clearProductsAndCalculations]);

  if (loading || isLoading) {
    return <SkeleteonCpCard />;
  }

  return (
    <Fragment>
      {formData.map((item: any, pIndex: number) => (
        <Fragment key={pIndex}>
          {opened?.includes(pIndex) ? (
            <TreatmentCard
              pIndex={pIndex}
              item={item}
              handleSearch={handleSearch}
              formData={formData}
              productOptions={productOptions}
              loading={loading}
              isLoading={isLoading}
              handleRemoveTreatment={handleRemoveTreatment}
              handleChangeFormData={handleChangeFormData}
              searchLoading={searchLoading}
            />
          ) : (
            <TreatmentDetailsCard
              pIndex={pIndex}
              item={item}
              handleRemoveTreatment={handleRemoveTreatment}
              handleEdit={handleEdit}
            />
          )}
        </Fragment>
      ))}
      <Button
        type="dashed"
        style={{
          width: "100%",
          marginTop: "5px",
          background: "transparent",
          borderColor: "#0071CD",
          color: "#0071CD",
        }}
        onClick={handleAddTreatment}
      >
        {formatTranslation(
          t("createProtocol.products.biologicals.product.addTreatment")
        )}
      </Button>
    </Fragment>
  );
};

export default SelectCpProduct;
